import React, { useEffect, useState, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import { X as CloseIcon, Calendar as CalendarIcon, Search as SearchIcon } from "lucide-react";
import { Axios } from "api/axios";
import { MecordColors } from "Constants/Color";
import MasterTableContainer from "../../TagsSearchPage/MasterFiltersTable/MasterTableContainer";
import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { mergeDashboardFilters, DashboardColFilter, scopedDropdownProps } from "./dashboardSidebarFilters";

interface DashboardChallanSidebarProps {
  open: boolean;
  onClose: () => void;
  type: "product" | "service";
  dateRange: {
    from_date: string;
    to_date: string;
  } | null;
  kpiId?: string;
}

export const DashboardChallanSidebar: React.FC<DashboardChallanSidebarProps> = ({
  open,
  onClose,
  type,
  dateRange,
  kpiId = "challan_created",
}) => {
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [filterValue, setFilterValue] = useState<DashboardColFilter[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const baseKpi = kpiId.replace("pipeline_", "");

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  const endpointForKpi = () => {
    if (baseKpi === "accessories_used") return "/dashboard/service/individual/accessories-used";
    if (baseKpi === "accessories_returned") return "/dashboard/service/individual/accessories-returned";
    return "/dashboard/service/individual/challans";
  };

  const fetchChallanData = async () => {
    if (!dateRange) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        from_date: dateRange.from_date,
        to_date: dateRange.to_date,
        period: "custom",
        page: String(page + 1),
        page_size: String(rowsPerPage),
      });
      if (kpiId) params.set("kpi_id", kpiId);
      if (search) params.set("search", search);
      if (sortField) {
        params.set(
          "ordering",
          sortDirection === "desc" ? `-${sortField}` : sortField,
        );
      }

      const filters = mergeDashboardFilters({}, filterValue);

      const response = await Axios.post(
        `${endpointForKpi()}?${params.toString()}`,
        { filters },
      );
      setData(response.data?.data || []);
      setTotalCount(response.data?.totalRecords || 0);
    } catch (error) {
      console.error("Failed to fetch challan/accessory data inside dashboard sidebar:", error);
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    if (open && dateRange) {
      fetchChallanData();
    }
  }, [
    open,
    type,
    dateRange,
    page,
    rowsPerPage,
    kpiId,
    search,
    filterValue,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    if (open) {
      setPage(0);
      setSearchInput("");
      setSearch("");
      setSorting([]);
      setColumnFilters([]);
      setFilterValue([]);
    }
  }, [open, type, dateRange, kpiId]);

  useEffect(() => {
    if (open) setPage(0);
  }, [filterValue, sortField, sortDirection]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  const themeColor = MecordColors?.ThemeBGColor || "#d2363a";
  const headerBgColor = MecordColors?.LightThemeBGColor || "#f8e8e8";

  const dateExtra = useMemo(
    () =>
      dateRange
        ? {
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
            period: "custom",
          }
        : undefined,
    [dateRange],
  );

  const columns = useMemo(() => {
    if (baseKpi === "challan_created") {
      return [
        {
          header: "Challan No.",
          accessorKey: "challan_no",
          enableSorting: true,
          ...scopedDropdownProps("challans", "challan_no", {}, dateExtra),
          cell: ({ row }: { row: any }) => (
            <span style={{ fontWeight: 600, color: themeColor }}>
              {row.original.challan_no || "-"}
            </span>
          ),
        },
        {
          header: "Order No.",
          accessorKey: "sales_order_number",
          enableSorting: true,
          ...scopedDropdownProps("challans", "sales_order_number", {}, dateExtra),
        },
        {
          header: "Date",
          accessorKey: "challan_date",
          enableSorting: true,
          filterType: "date" as const,
          cell: ({ row }: { row: any }) => formatDate(row.original.challan_date),
        },
        {
          header: "Customer Name",
          accessorKey: "customer_name",
          enableSorting: true,
          ...scopedDropdownProps("challans", "customer_name", {}, dateExtra),
        },
        {
          header: "Status",
          accessorKey: "status",
          enableSorting: true,
          ...scopedDropdownProps("challans", "status", {}, dateExtra),
        },
      ];
    }

    if (kpiId === "accessories_used") {
      return [
        {
          header: "Accessory",
          accessorKey: "product_name",
          enableSorting: true,
          ...scopedDropdownProps("accessories_used", "product_name", {}, dateExtra),
          cell: ({ row }: { row: any }) => (
            <span style={{ fontWeight: 600, color: themeColor }}>
              {row.original.product_name || "-"}
            </span>
          ),
        },
        {
          header: "SKU",
          accessorKey: "product_sku",
          enableSorting: true,
          ...scopedDropdownProps("accessories_used", "product_sku", {}, dateExtra),
        },
        {
          header: "Make / Model",
          accessorKey: "make_name",
          enableSorting: true,
          ...scopedDropdownProps("accessories_used", "make_name", {}, dateExtra),
          cell: ({ row }: { row: any }) =>
            `${row.original.make_name || "-"} / ${row.original.model_name || "-"}`,
        },
        {
          header: "UOM",
          accessorKey: "unit_name",
          enableSorting: true,
          ...scopedDropdownProps("accessories_used", "unit_name", {}, dateExtra),
        },
        {
          header: "Qty",
          accessorKey: "quantity",
          enableSorting: true,
          enableColumnFilter: false,
        },
        {
          header: "Order No.",
          accessorKey: "sales_order_number",
          enableSorting: true,
          ...scopedDropdownProps(
            "accessories_used",
            "sales_order_number",
            {},
            dateExtra,
          ),
        },
        {
          header: "Customer",
          accessorKey: "customer_name",
          enableSorting: true,
          ...scopedDropdownProps(
            "accessories_used",
            "customer_name",
            {},
            dateExtra,
          ),
        },
        {
          header: "Order Date",
          accessorKey: "order_date",
          enableSorting: true,
          filterType: "date" as const,
          cell: ({ row }: { row: any }) => formatDate(row.original.order_date),
        },
      ];
    }

    if (baseKpi === "accessories_returned") {
      return [
        {
          header: "RAGRN No.",
          accessorKey: "ragrn_no",
          enableSorting: true,
          ...scopedDropdownProps(
            "accessories_returned",
            "ragrn_no",
            {},
            dateExtra,
          ),
          cell: ({ row }: { row: any }) => (
            <span style={{ fontWeight: 600, color: themeColor }}>
              {row.original.ragrn_no || "-"}
            </span>
          ),
        },
        {
          header: "Challan No.",
          accessorKey: "challan_no",
          enableSorting: true,
          ...scopedDropdownProps(
            "accessories_returned",
            "challan_no",
            {},
            dateExtra,
          ),
        },
        {
          header: "Order No.",
          accessorKey: "sales_order_number",
          enableSorting: true,
          ...scopedDropdownProps(
            "accessories_returned",
            "sales_order_number",
            {},
            dateExtra,
          ),
        },
        {
          header: "Customer",
          accessorKey: "customer_name",
          enableSorting: true,
          ...scopedDropdownProps(
            "accessories_returned",
            "customer_name",
            {},
            dateExtra,
          ),
        },
        {
          header: "Inward Date",
          accessorKey: "inward_date",
          enableSorting: true,
          filterType: "date" as const,
          cell: ({ row }: { row: any }) => formatDate(row.original.inward_date),
        },
        {
          header: "Items",
          accessorKey: "item_count",
          enableSorting: false,
          enableColumnFilter: false,
        },
        {
          header: "Total Qty",
          accessorKey: "total_qty",
          enableSorting: false,
          enableColumnFilter: false,
        },
        {
          header: "Received By",
          accessorKey: "received_by",
          enableSorting: true,
          ...scopedDropdownProps(
            "accessories_returned",
            "received_by",
            {},
            dateExtra,
          ),
        },
        {
          header: "Status",
          accessorKey: "status",
          enableSorting: true,
          ...scopedDropdownProps("accessories_returned", "status", {}, dateExtra),
        },
      ];
    }

    return [
      {
        header: "Product / Accessory",
        accessorKey: "product_name",
        cell: ({ row }: { row: any }) => (
          <span style={{ fontWeight: 600, color: themeColor }}>
            {row.original.product_name || row.original.model_name || "-"}
          </span>
        ),
      },
    ];
  }, [baseKpi, themeColor, dateExtra]);

  const getTitle = () => {
    if (baseKpi === "accessories_used") return "Accessories Used";
    if (baseKpi === "accessories_returned") return "Accessories Returned";
    return "Service Challans Created";
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
          },
        },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          "@media (min-width: 768px)": {
            width: "calc(100vw - var(--tb-vertical-menu-width, 250px))",
          },
          p: 3,
          background: "linear-gradient(135deg, #ffffff 0%, #fefefe 50%, #f8f9fa 100%)",
          borderTopLeftRadius: "20px",
          borderBottomLeftRadius: "20px",
          borderLeft: "1px solid rgba(0, 0, 0, 0.08)",
          boxShadow: "-16px 0 48px rgba(0, 0, 0, 0.16), -4px 0 12px rgba(0, 0, 0, 0.08), inset 1px 0 0 rgba(255, 255, 255, 0.8)",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, pb: 1.5, boxSizing: "border-box", fontFamily: "'Poppins', sans-serif" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 2,
            borderBottom: "1px solid #e5e7eb",
            mb: 2.5,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontFamily: "'Poppins', sans-serif",
                  color: "#111827",
                  fontSize: "18px",
                }}
              >
                {getTitle()}
              </Typography>
              <Chip
                  label="Service"
                size="small"
                sx={{
                  backgroundColor: headerBgColor,
                  color: themeColor,
                  fontWeight: 600,
                  fontSize: "11px",
                  height: 20,
                  borderRadius: "6px",
                  fontFamily: "'Poppins', sans-serif"
                }}
              />
            </Box>
            {dateRange && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#6b7280" }}>
                <CalendarIcon size={12} />
                <Typography sx={{ fontSize: "11px", fontFamily: "'Poppins', sans-serif" }}>
                  Period: {formatDate(dateRange.from_date)} to {formatDate(dateRange.to_date)}
                </Typography>
              </Box>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: "#9ca3af" }}>
            <CloseIcon size={20} />
          </IconButton>
        </Box>

        {/* Search bar */}
        <Box sx={{ mb: 2.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={
              baseKpi === "accessories_used"
                ? "Search by accessory, SKU, order no, customer..."
                : baseKpi === "accessories_returned"
                ? "Search by RAGRN, return no, challan, order, customer..."
                : "Search by challan / accessory name..."
            }
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={18} style={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    sx={{ color: "#9ca3af" }}
                  >
                    <CloseIcon size={16} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#f9fafb",
                fontFamily: "'Poppins', sans-serif",
                fontSize: "13px",
                "& fieldset": {
                  borderColor: "#e5e7eb",
                },
                "&:hover fieldset": {
                  borderColor: "#d1d5db",
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColor,
                },
              },
            }}
          />
        </Box>

        {/* Listing Table Area */}
        <Box
          className="sidebar-table-scroll-container"
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.02)",
            position: "relative"
          }}
        >
          <MasterTableContainer
            columns={columns}
            data={data}
            totalRecords={totalCount}
            pageIndex={page}
            pageSize={rowsPerPage}
            onPageChange={setPage}
            onPageSizeChange={setRowsPerPage}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting={true}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            colValue={setFilterValue}
          />
        </Box>
      </Box>
    </Drawer>
  );
};
